import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlatList, View, ActivityIndicator, Image } from 'react-native';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { createStyleSheet, useStyles, UnistylesRuntime } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/Auth';
import MyAppText from '../../components/MyAppText';
import moment from 'moment';
import { translate } from '../../services/translate';
import Toast from 'react-native-toast-message';
import { useForm, Controller } from 'react-hook-form';
import Container from '../../components/Container';
import Header from '../../components/Header';
import { centralAPI } from '../../services/central-api';
import Card from '../../components/Card';
import FilePreview from '../../components/chat/FilePreview';
import Divider from '../../components/Divider';
import Badge from '../../components/Badge';
import ChatBubble from '../../components/chat/ChatBubble';
import ExpandableCommentInput from '../../components/ExpandableCommentInput';
import { UserParamList } from '../../typings/Params';

interface TicketForm {
    comment: string;
}

type DateBadge = {
    type: 'date-badge';
    date: string;
    id: string;
};

type CommentOrBadge = SupportTicketComment | DateBadge;

export default function GetOwnSupportTicket() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<UserParamList, 'GetOwnSupportTicket'>>();
    const navigation = useNavigation<NavigationProp<UserParamList>>();
    const index = useNavigationState(state => state);
    const { user: authenticatedUser } = useAuth();
    const isFocused = useIsFocused();
    const [ticket, setTicket] = useState<SupportTicket>();
    const [comments, setComments] = useState<SupportTicketComment[]>();
    const [users, setUsers] = useState<{ [userId: string]: SupportTicketUser | undefined; }>({});
    const [files, setFiles] = useState<File[]>([]);
    const flatListRef = useRef<FlatList<CommentOrBadge>>(null);
    const hasScrolledRef = useRef(false);
    const otherUser = Object.values(users).find(u => u?.role !== 'end-user');

    const ticketId = route.params?.id;

    const ticketDetailQuery = useQuery({
        queryKey: ['TicketDetailComponent', ticketId],
        queryFn: () => centralAPI.getOwnSupportTicketById(ticketId!),
        enabled: !!ticketId && !!authenticatedUser,
    });

    const ticketsCommentsQuery = useQuery({
        queryKey: ['supportTicketComments', ticketId],
        queryFn: () => centralAPI.getOwnSupportTicketComments(ticketId!),
        enabled: !!ticketId && !!authenticatedUser,
    });

    const defaultEmptyValues: TicketForm = {
        comment: ''
    };

    function handleScrollToEnd(width: number, height: number) {
        if (flatListRef.current) {
            flatListRef.current.scrollToOffset({ offset: height });
        }
    }

    const form = useForm<TicketForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const { control, getValues, reset, trigger } = form;

    useEffect(() => {
        if (!ticketDetailQuery.data) {
            return;
        }
        setTicket(ticketDetailQuery.data);
    }, [ticketDetailQuery.data]);

    async function getUsers(authors: number[]) {
        const newUsers: { [userId: string]: SupportTicketUser; } = {};
        for (const author of authors) {
            if (!(String(author) in users)) {
                const user = await centralAPI.getTicketUser(ticketId!, author);
                newUsers[String(author)] = user;
                setUsers({ ...users, ...newUsers });
            }
        }
    }

    useEffect(() => {
        hasScrolledRef.current = false;
    }, [ticketId]);

    useEffect(() => {
        if (!ticketsCommentsQuery.data) {
            return;
        }
        setComments(ticketsCommentsQuery.data.rows);
        const authors = [...new Set(ticketsCommentsQuery.data.rows.map(c => c.authorId))];
        getUsers(authors);
    }, [ticketsCommentsQuery.data]);

    useFocusEffect(useCallback(() => {
        if (isFocused && ticketId && authenticatedUser) {
            ticketsCommentsQuery.refetch();
            ticketDetailQuery.refetch();
        }
    }, [isFocused, ticketId, authenticatedUser]));

    function getCommentsWithDateBadges(): CommentOrBadge[] {
        if (!comments || comments.length === 0) {
            return [];
        }
        const result: CommentOrBadge[] = [];
        let lastDate: string | null = null;

        comments.forEach((comment, index) => {
            const commentDate = moment(comment.createdAt).format('YYYY-MM-DD');

            if (lastDate !== commentDate) {
                result.push({
                    type: 'date-badge',
                    date: commentDate,
                    id: `badge-${commentDate}-${index}`
                });
                lastDate = commentDate;
            }

            result.push(comment);
        });

        return result;
    }

    function renderComment({ item, index }: { item: CommentOrBadge; index: number; }) {
        if ('type' in item && item.type === 'date-badge') {
            const today = moment().format('YYYY-MM-DD');
            const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');

            let dateText = moment(item.date).format('DD/MM/YYYY');

            if (item.date === today) {
                dateText = translate('today');
            } else if (item.date === yesterday) {
                dateText = translate('yesterday');
            }

            return (
                <View style={styles.dateBadge}>
                    <Badge
                        backgroundColor={theme.colors.chat.badgeBackground}
                        textColor={theme.colors.chat.badgeTextColor}
                        style={{ borderColor: theme.colors.neutral[200], borderWidth: 1 }}
                        mode='ROUNDED'
                    >
                        {dateText}
                    </Badge>
                </View>
            );
        }

        const comment = item as SupportTicketComment;
        const user = users[String(comment.authorId)];
        const isSupport = user?.role !== 'end-user';

        const commentsWithBadges = getCommentsWithDateBadges();
        const previousItem = index > 0 ? commentsWithBadges[index - 1] : null;
        let previousIsSupport: boolean | null = null;

        if (previousItem && !('type' in previousItem)) {
            const previousComment = previousItem as SupportTicketComment;
            const previousUser = users[String(previousComment.authorId)];
            previousIsSupport = previousUser?.role !== 'end-user';
        }

        const marginTop = previousIsSupport === null || previousIsSupport === isSupport ? 8 : 16;


        return (
            <View style={{
                width: '100%',
                justifyContent: !isSupport ? 'flex-end' : 'flex-start',
                marginTop
            }}>
                <ChatBubble
                    comment={comment}
                    user={user}
                    isSupport={isSupport}
                    isOwnMessage={!isSupport}
                />
            </View>
        );
    }

    function getItemKey(item: CommentOrBadge): string {
        if ('type' in item && item.type === 'date-badge') {
            return item.id;
        }
        return String((item as SupportTicketComment).id);
    }

    async function formSubmit() {
        try {
            const isFormValid = await trigger();

            if (!isFormValid) {
                throw new Error('invalid form');
            }

            const values = getValues();

            await centralAPI.replySupportTicket({
                ticketId: ticketId!,
                body: values.comment,
                files: files,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setFiles([]);
            reset();
            ticketsCommentsQuery.refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    if (!ticket) {
        return <></>;
    }

    return (
        <Container>
            <Header
                title={`#${ticket.id} - ${ticket.subject}`}
                goBackAction={() => {
                    if (index.routes.length > 1) {
                        return navigation.goBack();
                    } else {
                        window.location.href = '/user/support-tickets';
                    }
                }}
            />
            <Card style={{ padding: 0 }}>
                {ticket && (ticketsCommentsQuery.isLoading || ticketDetailQuery.isLoading) ? (
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                ) : (
                    <>
                        {otherUser && (
                            <View>
                                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', padding: 16 }}>
                                    {UnistylesRuntime.themeName === 'dark' ? (
                                        <Image
                                            style={styles.userFrame}
                                            source={require('../../../assets/SentinelX_chat_dark.png')}
                                        />
                                    ) : (
                                        <Image
                                            style={styles.userFrame}
                                            source={require('../../../assets/SentinelX_pin.png')}
                                        />
                                    )}
                                    {otherUser.name.includes('Sentinel') ? (
                                        <MyAppText style={styles.sentinelName}>
                                            {otherUser.name}
                                        </MyAppText>
                                    ) : (
                                        <MyAppText style={styles.userName}>
                                            {otherUser.name}
                                        </MyAppText>
                                    )}
                                </View>
                                <Divider color={theme.colors.neutral['200']} style={{ zIndex: -1 }} />
                            </View>
                        )}

                        <View style={{ flex: 1, backgroundColor: theme.colors.chat.background, paddingHorizontal: 16, paddingVertical: 24 }}>
                            <View style={styles.openBadge}>
                                <Badge
                                    backgroundColor={theme.colors.chat.badgeBackground}
                                    textColor={theme.colors.white[500]}
                                    style={{ borderColor: theme.colors.neutral[200], borderWidth: 1 }}
                                    mode='ROUNDED'
                                >
                                    <MyAppText>{translate('ticketOpenAt')}: {moment(ticketDetailQuery.data?.createdAt).format('DD/MM/YYYY')}</MyAppText>

                                </Badge>
                            </View>

                            <View style={{ flex: 1 }}>
                                <FlatList
                                    ref={flatListRef}
                                    data={getCommentsWithDateBadges()}
                                    renderItem={renderComment}
                                    keyExtractor={getItemKey}
                                    contentContainerStyle={{
                                        width: '100%',
                                        alignItems: 'stretch'
                                    }}
                                    onContentSizeChange={handleScrollToEnd}
                                    style={{ flex: 1 }}
                                />
                            </View>


                        </View>
                        <View>
                            <Divider color={theme.colors.neutral['200']} style={{ zIndex: -1 }} />
                            {ticket.status !== 'closed' && (
                                <Controller
                                    control={control}
                                    name="comment"
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange } }) => (
                                        <View style={{ flex: 1, paddingHorizontal: 16, paddingVertical: 8 }}>
                                            <ExpandableCommentInput
                                                value={value}
                                                onChangeText={onChange}
                                                files={files}
                                                onFilesChange={setFiles}
                                                onSubmit={formSubmit}
                                                placeholder={translate('comment')}
                                                required={true}
                                                submitButtonText={translate('send')}
                                                FilePreviewComponent={FilePreview}
                                                maxFiles={5}
                                                acceptedFileTypes={[
                                                    'image/png',
                                                    'image/jpg',
                                                    'image/jpeg',
                                                    'application/pdf',
                                                ]}
                                                maxFileSize={10}
                                                maxTotalSize={50}
                                                textAreaMinHeight={30}
                                            />
                                        </View>
                                    )}
                                />
                            )}
                        </View>
                    </>
                )}
            </Card>
        </Container >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    userFrame: {
        width: 32,
        height: 32,
        aspectRatio: 1,
        borderRadius: 50,
        backgroundColor: theme.colors.neutral[300],
        justifyContent: 'center',
        alignItems: 'center',
    },
    dateBadge: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 16
    },
    openBadge: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    userName: {
        fontWeight: '700',
        fontSize: 14,
        marginBottom: 4,
        color: theme.colors.chat.userAvatar,
    },
    sentinelName: {
        fontWeight: '700',
        fontSize: 14,
        marginBottom: 4,
        color: theme.colors.chat.sentinelAvatar,
        // seus estilos para autor do comentário
    },
}));
